import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CircularProgress from "@mui/material/CircularProgress";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { ReactNode, useState, useRef, useEffect } from "react";
import { COLOR_BLACK } from "shared/theme/colors";
import EmptyContent from "./emptyContent";
import {
  StyledPaper,
  StyledTable,
  StyledTableHead,
  StyledHeaderCell,
  StyledSpinnerWrapper,
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  StyledHeaderBox,
  StyledCountBadge,
  StyledGroupRow,
  StyledGroupCell,
  StyledNestedTable,
  StyledNestedTableBody,
  StyledEmptyCell,
  StyledLoadingBox,
  StyledColGroup,
  StyledCol,
  StyledHeaderRow,
} from "./styled";
import { TableColumn } from "./types";

export type CollapsibleTableProps<T> = {
  readonly columns: TableColumn<T>[];
  readonly groups: {
    title: string;
    count?: number;
    data: T[];
    initiallyExpanded?: boolean;
  }[];
  readonly noDataMessage?: string;
  readonly renderRow: (item: T) => ReactNode;
  readonly maxHeight?: string;
  readonly isLoading?: boolean;
};

const TABLE_HEADER_HEIGHT = 48;

function CollapsibleTable<T>({
  columns,
  groups,
  noDataMessage = "No data available",
  renderRow,
  maxHeight,
  isLoading = false,
}: CollapsibleTableProps<T>) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    Object.fromEntries(
      groups.map((group) => [group.title, group.initiallyExpanded !== false]),
    ),
  );

  useEffect(() => {
    setExpandedGroups((prev) => {
      const newState = { ...prev };

      groups.forEach((group) => {
        newState[group.title] ??= group.initiallyExpanded !== false;
      });

      return newState;
    });
  }, [groups]);

  const containerRef = useRef<HTMLDivElement>(null);

  const toggleGroup =
    (groupTitle: string, groupIndex: number) =>
    (event: React.SyntheticEvent, expanded: boolean) => {
      if (!expanded && containerRef.current) {
        requestAnimationFrame(() => {
          if (containerRef.current) {
            const groupElements =
              containerRef.current.querySelectorAll(".MuiAccordion-root");

            if (groupElements[groupIndex]) {
              const groupRect =
                groupElements[groupIndex].getBoundingClientRect();
              const containerRect =
                containerRef.current.getBoundingClientRect();

              const offset = groupRect.top - containerRect.top;

              if (offset < TABLE_HEADER_HEIGHT) {
                const adjustedScrollTop =
                  containerRef.current.scrollTop -
                  (TABLE_HEADER_HEIGHT - offset);

                containerRef.current.style.scrollBehavior = "smooth";
                containerRef.current.scrollTop = adjustedScrollTop - 1;

                setTimeout(() => {
                  if (containerRef.current) {
                    containerRef.current.style.scrollBehavior = "";
                  }
                }, 300);
              }
            }
          }
        });
      }

      setExpandedGroups((prev) => ({
        ...prev,
        [groupTitle]: expanded,
      }));
    };

  const totalItems = groups.reduce(
    (total, group) => total + group.data.length,
    0,
  );

  const hasNoData = totalItems === 0 || groups.every((group) => !group.count);

  const renderTableHeader = () => (
    <StyledTableHead>
      <StyledHeaderRow>
        {columns.map((column) => (
          <StyledHeaderCell
            key={column.key as string}
            width={column.width}
            align={column.align}
          >
            <Typography variant="bodyMSemiBold" color={COLOR_BLACK}>
              {column.title}
            </Typography>
          </StyledHeaderCell>
        ))}
      </StyledHeaderRow>
    </StyledTableHead>
  );

  if (isLoading) {
    return (
      <StyledPaper ref={containerRef} maxHeight={maxHeight} variant="outlined">
        <StyledTable stickyHeader>
          <StyledColGroup>
            {columns.map((column) => (
              <StyledCol
                key={column.key as string}
                columnWidth={column.width}
              />
            ))}
          </StyledColGroup>
          {renderTableHeader()}
        </StyledTable>
        <StyledLoadingBox>
          <StyledSpinnerWrapper>
            <CircularProgress size={32} />
          </StyledSpinnerWrapper>
        </StyledLoadingBox>
      </StyledPaper>
    );
  }

  if (hasNoData) {
    return <EmptyContent message={noDataMessage} />;
  }

  return (
    <StyledPaper ref={containerRef} maxHeight={maxHeight} variant="outlined">
      <StyledTable stickyHeader>
        <StyledColGroup>
          {columns.map((column) => (
            <StyledCol key={column.key as string} columnWidth={column.width} />
          ))}
        </StyledColGroup>
        {renderTableHeader()}
        <TableBody>
          {groups.map((group, index) => (
            <StyledGroupRow key={group.title}>
              <StyledGroupCell colSpan={columns.length}>
                <StyledAccordion
                  expanded={
                    expandedGroups[group.title] ??
                    group.initiallyExpanded !== false
                  }
                  onChange={toggleGroup(group.title, index)}
                  disableGutters
                  defaultExpanded={group.initiallyExpanded !== false}
                >
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${group.title}-content`}
                    id={`${group.title}-header`}
                    index={index}
                  >
                    <StyledHeaderBox>
                      <Typography variant="bodyMSemiBold" color={COLOR_BLACK}>
                        {group.title}
                      </Typography>
                      {group.count !== undefined && group.count > 0 && (
                        <StyledCountBadge
                          label={group.count}
                          color="primary"
                          size="small"
                        />
                      )}
                    </StyledHeaderBox>
                  </StyledAccordionSummary>
                  <StyledAccordionDetails>
                    <StyledNestedTable>
                      <StyledColGroup>
                        {columns.map((column) => (
                          <StyledCol
                            key={column.key as string}
                            columnWidth={column.width}
                          />
                        ))}
                      </StyledColGroup>
                      <StyledNestedTableBody>
                        {group.data.length === 0 ? (
                          <TableRow>
                            <StyledEmptyCell colSpan={columns.length}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No content scheduled for {group.title}.
                              </Typography>
                            </StyledEmptyCell>
                          </TableRow>
                        ) : (
                          group.data.map(renderRow)
                        )}
                      </StyledNestedTableBody>
                    </StyledNestedTable>
                  </StyledAccordionDetails>
                </StyledAccordion>
              </StyledGroupCell>
            </StyledGroupRow>
          ))}
        </TableBody>
      </StyledTable>
    </StyledPaper>
  );
}

export default CollapsibleTable;
